// Expedition + party tuning values (Phase 5 Step 11).
//
// Composed back into `balance` via balance.ts as:
//   expedition: balanceExpedition.expedition
//
// Per DECISIONS 2026-04-18 "Phase 4 Step 18 balance-*.ts sibling-file
// split": each new subsystem's tuning lives in a balance-<subsystem>.ts
// sibling, composed via property assignment (not spread). This file is
// the canonical home for expedition lifecycle + party-protocol tuning
// knobs. Per-archetype geometry + per-mob stats stay in
// server/src/expedition-mob.ts and shared/mob-types.ts (data catalogs,
// same reasoning as shared/recipes.ts); this surface carries only
// cross-cutting tuning knobs.

export const balanceExpedition = {
  expedition: {
    // Hard cap on party size (leader + members). Invite beyond this
    // returns 'party_full'. Minimum is enforced at expedition-entry
    // time in Step 12; inviting below the minimum is allowed.
    maxPartySize: 6,
    minPartySize: 2,

    // Invite time-to-live. An 'invited' row older than this is pruned
    // lazily on getPartyRoster reads (no alarm — spec-locked lazy TTL)
    // and acceptPartyInvite returns 'invite_expired' for a stale row.
    partyInviteTtlMs: 60 * 1000,

    // Derelict Ship archetype counts. Authoritative data for room
    // bounds + per-room mob plans lives inline in
    // server/src/expedition-mob.ts (data catalog, not tuning surface);
    // these mirrors exist so tests and future archetype validators can
    // assert the shape without importing the server module.
    derelictShipRooms: 3,
    mobsPerRoom: 2,

    // Phase 5 Step 14b — post-completion grace window. After boss-kill
    // flips status='completed', the expedition sits idle for this long
    // before the alarm evicts members + closes sockets. 60s matches a
    // typical loot-review + voluntary-exit rhythm without pinning the
    // DO for minutes on a disconnected party.
    postCompletionGraceMs: 60 * 1000,

    // Phase 5 Step 14b — cadence of the abandon/completion alarm.
    // 5 min is coarse enough to be cheap (one wake per DO per cadence
    // while active) and fine enough that a 30-min abandon timeout
    // fires within 35 min worst-case.
    alarmCheckCadenceMs: 5 * 60 * 1000,

    // Phase 7 Block F — proximity invite radius. Players within this
    // many pixels of the inviter appear in the nearby-players list when
    // the party panel is open. Measured as Euclidean distance using the
    // zone roster's x/y coordinates.
    proximityRangePx: 200,

    // Phase 7 Block F — online heuristic for the friends list.
    // A friend whose last_seen is within this window is shown as online.
    friendOnlineWindowMs: 60 * 1000,
  },
} as const;
